import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useOutletContext, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { INVESTING_SYMBOL_RE, investingApi } from "../../services/api";
import { withoutBidiControls } from "../snippets/bidi";
import { apiErrorMessage } from "../../services/apiError";
import DiscussDialog from "./DiscussDialog";
import DiscussionPanel from "./DiscussionPanel";
import NoteCard from "./NoteCard";
import ResearchThisDialog from "./ResearchThisDialog";
import RetractNoteDialog from "./RetractNoteDialog";
import useTheses from "./useTheses";
import useUnlinked from "./useUnlinked";
import VerdictDialog from "./VerdictDialog";
import VerdictPanel from "./VerdictPanel";
import {
  companyPath,
  formatDay,
  formatMarketCap,
  formatScore,
  noteAnchor,
  scoreTone,
  taskBoardPath,
  thesisPath,
} from "./lib/format";
import { screenFigures } from "./lib/screen";

const ACTION = "h-11 md:h-9";
const RESEARCH_ACTION = "investing-research-action";
const retractId = (noteId) => `retract-note-${noteId}`;
const DISCUSS_ACTION = "investing-discuss-action";

/** The current scores, each with the rationale of the note it's from. */
function CurrentScores({ company, thesisTitle, listedNotes }) {
  const scores = Array.isArray(company.scores) ? company.scores : [];
  // Agent-written: without bidi controls, as on the note itself.
  const rationale = (score) =>
    withoutBidiControls(
      company.notes?.find((n) => n.id === score.note)?.scores?.find((s) => s.thesis === score.thesis)?.rationale,
    );
  return (
    <section aria-labelledby="investing-scores" className="rounded-lg border bg-card p-4">
      <h2 id="investing-scores" className="mt-0 mb-1 text-lg font-semibold">
        Scores
      </h2>
      <p className="mt-0 mb-3 text-sm text-muted-foreground">
        Where it stands on each thesis, from −2 to +2 — the newest note that scored it.
      </p>
      {scores.length === 0 ? (
        <p className="m-0 text-sm">
          No scores yet — research scores the company against each thesis that applies.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {scores.map((score) => {
            const why = rationale(score);
            return (
              <li key={score.thesis} className="text-sm">
                <Link
                  to={thesisPath(score.thesis)}
                  className="inline-flex min-h-11 items-center font-medium underline-offset-2 wrap-anywhere hover:underline md:min-h-0"
                >
                  {thesisTitle(score.thesis)}
                </Link>{" "}
                <span className={cn("font-semibold tabular-nums", scoreTone(score.score))}>
                  {formatScore(score.score)}
                </span>
                {listedNotes.has(score.note) ? (
                  <span className="block wrap-anywhere">
                    {why ? `${why} — ` : ""}from{" "}
                    <a href={`#${noteAnchor(score.note)}`} className="underline underline-offset-2">
                      note #{score.note}
                    </a>
                  </span>
                ) : (
                  <span className="block text-muted-foreground">
                    From note #{score.note}, older than the notes listed below.
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

CurrentScores.propTypes = {
  company: PropTypes.shape({ scores: PropTypes.array, notes: PropTypes.array }).isRequired,
  thesisTitle: PropTypes.func.isRequired,
  listedNotes: PropTypes.instanceOf(Set).isRequired,
};

/** The screen's figures, "as of" the import. */
function ScreenFigures({ company }) {
  const figures = screenFigures(company.screen);
  const day = formatDay(company.screened_at);
  return (
    <section aria-labelledby="investing-screen" className="rounded-lg border bg-card p-4">
      <h2 id="investing-screen" className="mt-0 mb-1 text-lg font-semibold">
        Screen figures
      </h2>
      {figures.length === 0 ? (
        <p className="m-0 text-sm">
          {day
            ? `No screen figures yet — the screen of ${day} had none for ${company.symbol}.`
            : "No screen figures yet — they arrive with the board task “Investing: screen the Russell 1000”."}
        </p>
      ) : (
        <>
          <p className="mt-0 mb-3 text-sm text-muted-foreground">
            {day ? `As of ${day}` : "Date unknown"}
            {company.universe_source ? `, from the ${company.universe_source} screen` : ""}. As the scanner
            reported them: margins and growth are worked out from its ratios, and a figure like a Forward P/E of
            5,000 is its placeholder, not a measurement.
          </p>
          <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {figures.map((figure) => (
              <div key={figure.key} className="flex justify-between gap-3 border-b border-border py-1">
                <dt className="text-muted-foreground">{figure.label}</dt>
                <dd className="m-0 text-right font-medium tabular-nums">{figure.text}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </section>
  );
}

ScreenFigures.propTypes = {
  company: PropTypes.shape({
    symbol: PropTypes.string,
    screen: PropTypes.object,
    screened_at: PropTypes.string,
    universe_source: PropTypes.string,
  }).isRequired,
};

/**
 * A company's page (spec §5.2; walkthroughs 2, 3, 4 and 6), at
 * /investing/research/:symbol.
 *
 * - The verdict panel: the verdict and who set it when and why, a pending
 *   proposal ("Proposed: Own (4) — awaiting your decision") with Decide on
 *   this proposal, Edit verdict, and the newest decisions ("2 of 34").
 * - The discussion once one is linked: its latest post and pinned decision.
 * - The current scores, each with the rationale of the note it's from and
 *   a link to its thesis's page.
 * - The research notes: the newest 20 with how many in all, retracted ones
 *   only when asked; summaries are agent-written markdown, rendered
 *   untrusted (D8); evidence with each web source's host.
 * - The screen figures "as of" their date.
 *
 * Who sees which control, failing closed while `me` is unknown:
 * - Edit verdict and Discuss: anyone signed in — the server judges (D6
 *   refuses an agent a verdict a person set; the dialog shows its words).
 * - Retract and Research this: a person only. Retract is human-gated on
 *   the server (spec §2.1); Research this needs the person's agents.
 *
 * Every change answers with the company's page, which replaces this one —
 * unless retracted notes are shown (the answer leaves them out), or a load
 * was in flight (it would land stale): then the page is read again. Only
 * the newest read lands.
 *
 * A research task or discussion whose link failed is in the section's
 * store (`unlinked`, InvestingLayout), by symbol: this page and the list
 * offer "Link task #N" / "Link discussion #N" — never a second one — while
 * this tab remembers it, and another company's page never offers it.
 */
function InvestingCompany() {
  const { symbol: raw = "" } = useParams();
  const location = useLocation();
  const outlet = useOutletContext() || {};
  const { me } = outlet;
  // The section's store; on its own (outside the layout), this page's.
  const ownUnlinked = useUnlinked(null);
  const unlinked = outlet.unlinked ?? ownUnlinked;
  const { setTask, setDiscussion } = unlinked;
  const known = me?.kind === "human" || me?.kind === "agent";
  const isPerson = me?.kind === "human";
  const upper = raw.toUpperCase();
  const valid = INVESTING_SYMBOL_RE.test(raw);

  const { theses, loading: thesesLoading, error: thesesError, refresh: refreshTheses } = useTheses();
  const titles = useMemo(() => new Map(theses.map((t) => [t.slug, t.title])), [theses]);
  const thesisTitle = useCallback((slug) => titles.get(slug) ?? slug, [titles]);
  const retiredTheses = useMemo(() => new Set(theses.filter((t) => t.status === "retired").map((t) => t.slug)), [theses]);
  const thesisRetired = useCallback((slug) => retiredTheses.has(slug), [retiredTheses]);

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(valid);
  const [failure, setFailure] = useState(null); // {notFound} | {message}
  const [showRetracted, setShowRetracted] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [status, setStatus] = useState("");

  // Only the newest read lands; `reading` says one is out.
  const requestRef = useRef(0);
  const reading = useRef(false);
  const showRetractedRef = useRef(showRetracted);
  showRetractedRef.current = showRetracted;

  // A different company starts clean.
  useEffect(() => {
    setCompany(null);
    setFailure(null);
    setStatus("");
    setShowRetracted(false);
  }, [raw]);

  useEffect(() => {
    if (!valid) {
      setLoading(false);
      return;
    }
    const request = ++requestRef.current;
    reading.current = true;
    setLoading(true);
    investingApi.companies.get(raw, { retracted: showRetracted }).then(
      ({ data }) => {
        if (request !== requestRef.current) return;
        reading.current = false;
        setCompany(data);
        setFailure(null);
        setLoading(false);
      },
      (err) => {
        if (request !== requestRef.current) return;
        reading.current = false;
        setFailure(
          err?.response?.status === 404
            ? { notFound: true }
            : { message: apiErrorMessage(err, "Couldn't load this company.") },
        );
        setLoading(false);
      },
    );
  }, [raw, valid, showRetracted, reloadTick]);

  /** A change's answer: the company's page as the API returned it. */
  const applyPage = useCallback((data) => {
    if (!showRetractedRef.current) setCompany(data);
    // It leaves retracted notes out, and a read already out would land stale.
    if (showRetractedRef.current || reading.current) setReloadTick((tick) => tick + 1);
  }, []);

  // A dialog hands focus back to its opener (ui/dialog). When the change
  // took the opener away (Decide on this proposal, Retract, Research this,
  // Discuss), focus goes to the control that took its place, else to the
  // section it changed.
  const opener = useRef(null);
  const remember = () => {
    opener.current = document.activeElement;
  };
  const focusAfter = (ids, fallback) => (event) => {
    if (opener.current?.isConnected) return;
    const target = ids.map((id) => document.getElementById(id)).find(Boolean) ?? fallback?.current;
    if (target) {
      event.preventDefault();
      target.focus();
    }
  };

  // A link to a note (/investing/research/NVDA#note-12) lands on it once the
  // notes are there.
  const scrolledTo = useRef(null);
  useEffect(() => {
    const hash = location.hash;
    if (!company || !/^#note-\d+$/.test(hash) || scrolledTo.current === hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    scrolledTo.current = hash;
    target.scrollIntoView?.({ block: "start" });
    target.focus?.({ preventScroll: true });
  }, [company, location.hash]);

  // --- Edit verdict --------------------------------------------------------
  const [verdictOpen, setVerdictOpen] = useState(false);
  const [verdictProposal, setVerdictProposal] = useState(null);
  const verdictHeading = useRef(null);
  const openVerdict = (proposal) => {
    remember();
    setVerdictProposal(proposal);
    setVerdictOpen(true);
  };
  const verdictSaved = (data) => {
    applyPage(data);
    setVerdictOpen(false);
    const conviction = data.conviction !== null && data.conviction !== undefined ? ` · ${data.conviction}` : "";
    const label = { watch: "Watch", own: "Own", avoid: "Avoid" }[data.verdict] ?? data.verdict;
    setStatus(`Verdict saved: ${label}${conviction}. The history keeps your reason.`);
  };

  // --- Retract -------------------------------------------------------------
  const [retracting, setRetracting] = useState(null); // note id
  const [retractOpen, setRetractOpen] = useState(false);
  const notesHeading = useRef(null);
  const openRetract = (noteId) => {
    remember();
    setRetracting(noteId);
    setRetractOpen(true);
  };
  const retracted = (data) => {
    applyPage(data);
    setRetractOpen(false);
    setStatus(`Note #${retracting} retracted — its scores left the current scores.`);
  };

  // --- Research this -------------------------------------------------------
  const [researchOpen, setResearchOpen] = useState(false);
  const openResearch = () => {
    remember();
    setResearchOpen(true);
  };
  // The store is the signed-in person's (useUnlinked), and only a person
  // makes research tasks, so an agent's session never has one here.
  const unlinkedTask = unlinked.tasks[upper] ?? null;
  const researchLinked = (symbol, data, task) => {
    setTask(symbol, null);
    applyPage(data);
    setStatus(
      `Research requested — task #${task.id}${task.assignee?.username ? `, assigned to ${task.assignee.username}` : ""}. Its note lands on this page.`,
    );
  };

  // --- Discuss -------------------------------------------------------------
  const [discussOpen, setDiscussOpen] = useState(false);
  const openDiscuss = () => {
    remember();
    setDiscussOpen(true);
  };
  const unlinkedDiscussion = unlinked.discussions[upper] ?? null;
  const discussionHeading = useRef(null);
  // A different company (a history jump) closes any dialog left open: it
  // was opened for the last one.
  useEffect(() => {
    setVerdictOpen(false);
    setRetractOpen(false);
    setResearchOpen(false);
    setDiscussOpen(false);
  }, [raw]);

  const discussionLinked = (symbol, data, discussion) => {
    setDiscussion(symbol, null);
    applyPage(data);
    setStatus(`Discussion #${discussion.id} started and linked — its latest post shows here.`);
  };

  if (raw !== upper && INVESTING_SYMBOL_RE.test(upper)) {
    return <Navigate to={`${companyPath(upper)}${location.hash}`} replace />;
  }

  const back = (
    <Link
      to={`/investing/research${location.state?.researchSearch ?? ""}`}
      aria-label="Back to Research"
      className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:min-h-8"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Research
    </Link>
  );

  if (!valid || failure?.notFound) {
    return (
      <div className="page-container mt-6 pt-4">
        {back}
        <div className="rounded-lg border bg-card p-4">
          <p className="m-0">
            {valid
              ? `${raw} isn't in your research. The Russell 1000 screen adds companies, and so does research on one.`
              : `“${raw}” isn't a symbol — symbols are upper-case tickers like NVDA or BRK.B.`}
          </p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="page-container mt-6 pt-4">
        {back}
        {failure ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{failure.message}</span>{" "}
              <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => setReloadTick((t) => t + 1)}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="py-12 text-center" role="status">
            <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading {raw}…</span>
          </div>
        )}
      </div>
    );
  }

  const notes = Array.isArray(company.notes) ? company.notes : [];
  const listedNotes = new Set(notes.map((n) => n.id));
  const cap = formatMarketCap(company.market_cap);
  const facts = [company.sector, company.industry, cap ? `${cap} market cap` : "Market cap unknown"].filter(Boolean);
  const researched = formatDay(company.last_researched_at);
  const notesTotal = company.notes_total ?? notes.length;
  const hiddenRetracted = company.retracted_notes ?? 0;

  let research = null;
  if (company.research_requested && company.requested_task) {
    research = (
      <Link
        id={RESEARCH_ACTION}
        to={taskBoardPath(company.requested_task)}
        className="inline-flex min-h-11 items-center text-sm underline underline-offset-2 md:min-h-9"
      >
        Research requested — task #{company.requested_task}
      </Link>
    );
  } else if (unlinkedTask) {
    research = (
      <span className="flex flex-wrap items-center gap-2 text-sm">
        Task #{unlinkedTask.id} isn&apos;t linked yet.
        <Button id={RESEARCH_ACTION} type="button" variant="outline" className={ACTION} onClick={openResearch}>
          Link task #{unlinkedTask.id}
        </Button>
      </span>
    );
  } else if (isPerson) {
    research = (
      <Button id={RESEARCH_ACTION} type="button" variant="outline" className={ACTION} onClick={openResearch}>
        Research this
      </Button>
    );
  }

  let discuss = null;
  if (!company.discussion && known) {
    discuss = unlinkedDiscussion ? (
      <span className="flex flex-wrap items-center gap-2 text-sm">
        Discussion #{unlinkedDiscussion.id} isn&apos;t linked yet.
        <Button id={DISCUSS_ACTION} type="button" variant="outline" className={ACTION} onClick={openDiscuss}>
          Link discussion #{unlinkedDiscussion.id}
        </Button>
      </span>
    ) : (
      <Button id={DISCUSS_ACTION} type="button" variant="outline" className={ACTION} onClick={openDiscuss}>
        Discuss
      </Button>
    );
  }

  return (
    <div className="page-container mt-6 pt-4" aria-busy={loading || undefined}>
      <div className="max-w-4xl">
        {back}
        <header className="mb-4">
          <h1 className="mb-1 font-display text-3xl font-semibold tracking-tight">{company.symbol}</h1>
          <p className="m-0 text-lg wrap-anywhere">{company.name}</p>
          <p className="m-0 text-sm text-muted-foreground">
            {facts.join(" · ")}
            {!company.in_universe && (
              <>
                {" "}
                <Badge variant="outline" title="Taken out of the universe by hand">
                  De-listed
                </Badge>
              </>
            )}
          </p>
          <p className="m-0 text-sm text-muted-foreground">
            {researched ? `Last researched ${researched}` : "Not researched yet"}
          </p>
          {(research || discuss) && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {research}
              {discuss}
            </div>
          )}
        </header>

        <p role="status" className={cn("text-sm", status ? "mb-4" : "m-0")}>
          {status}
        </p>

        <div className="flex flex-col gap-4">
          <VerdictPanel
            ref={verdictHeading}
            company={company}
            thesisTitle={thesisTitle}
            thesisRetired={thesisRetired}
            listedNotes={listedNotes}
            onEdit={known ? openVerdict : null}
          />

          {company.discussion && <DiscussionPanel ref={discussionHeading} discussion={company.discussion} />}

          <CurrentScores company={company} thesisTitle={thesisTitle} listedNotes={listedNotes} />

          <section aria-labelledby="investing-notes">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 id="investing-notes" ref={notesHeading} tabIndex={-1} className="m-0 text-lg font-semibold outline-none">
                Research notes
              </h2>
              {hiddenRetracted > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 md:h-8"
                  onClick={() => setShowRetracted((shown) => !shown)}
                >
                  {showRetracted
                    ? "Hide retracted notes"
                    : `Show ${hiddenRetracted} retracted ${hiddenRetracted === 1 ? "note" : "notes"}`}
                </Button>
              )}
            </div>
            {notes.length === 0 ? (
              <div className="rounded-lg border bg-card p-4">
                <p className="m-0 text-sm">
                  No research yet. Research this puts a task on the board for one of your agents; its note — the
                  summary, the evidence, a score per thesis and any verdict it proposes — lands here.
                </p>
              </div>
            ) : (
              <>
                <p className="mt-0 mb-2 text-sm text-muted-foreground">
                  {notes.length < notesTotal
                    ? `The newest ${notes.length} of ${notesTotal} notes`
                    : `${notesTotal} ${notesTotal === 1 ? "note" : "notes"}`}
                  , newest first{showRetracted ? ", retracted ones included" : ""}.
                </p>
                <div className="flex flex-col gap-3">
                  {notes.map((item) => (
                    <NoteCard
                      key={item.id}
                      note={item}
                      thesisTitle={thesisTitle}
                      onRetract={isPerson ? openRetract : null}
                    />
                  ))}
                </div>
              </>
            )}
          </section>

          <ScreenFigures company={company} />
        </div>
      </div>

      {known && (
        <VerdictDialog
          open={verdictOpen}
          company={company}
          proposal={verdictProposal}
          theses={theses}
          thesesLoading={thesesLoading}
          thesesError={thesesError}
          onRetryTheses={refreshTheses}
          me={me}
          onOpenChange={setVerdictOpen}
          onSaved={verdictSaved}
          onCloseAutoFocus={focusAfter([], verdictHeading)}
        />
      )}
      {isPerson && (
        <RetractNoteDialog
          open={retractOpen}
          noteId={retracting}
          symbol={company.symbol}
          onOpenChange={setRetractOpen}
          onRetracted={retracted}
          onCloseAutoFocus={focusAfter([retractId(retracting), noteAnchor(retracting)], notesHeading)}
        />
      )}
      {isPerson && (
        <ResearchThisDialog
          open={researchOpen}
          company={{ symbol: company.symbol, name: company.name }}
          me={me}
          pendingTask={unlinkedTask}
          onOpenChange={setResearchOpen}
          onTaskCreated={setTask}
          onLinked={researchLinked}
          onForgetTask={(symbol) => setTask(symbol, null)}
          onCloseAutoFocus={focusAfter([RESEARCH_ACTION])}
        />
      )}
      {known && (
        <DiscussDialog
          open={discussOpen}
          company={{ symbol: company.symbol, name: company.name }}
          pending={unlinkedDiscussion}
          onOpenChange={setDiscussOpen}
          onCreated={setDiscussion}
          onLinked={discussionLinked}
          onForget={(symbol) => setDiscussion(symbol, null)}
          onCloseAutoFocus={focusAfter([DISCUSS_ACTION], discussionHeading)}
        />
      )}
    </div>
  );
}

export default InvestingCompany;
